import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { authenticatedFetch } from '../../utils/apiClient';
import TeacherComprehensionView from './ComprehensionResults';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft, ChevronRight, Upload, X, FileText, Award } from 'lucide-react';
import { cn } from '../../lib/utils';
import '../../styles/ComprehensionView.scss';
import logger from '../../utils/logger';
import LoadingAnecdotes from '../shared/LoadingAnecdotes';
import Loading from '../shared/Loading';
import { track } from '../../utils/analytics';

function ComprehensionView({ API_BASE_URL, authToken, userData, onSubmit, initialComprehension, onBack, onSubmissionComplete }) {
  const [comprehensions, setComprehensions] = useState([]);
  const [selectedComprehension, setSelectedComprehension] = useState(initialComprehension || null);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(!initialComprehension);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [imageFiles, setImageFiles] = useState({});
  const [processingImages, setProcessingImages] = useState({});
  const [submissionId, setSubmissionId] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  // State for collapsible sections
  const [isPastExamsCollapsed, setIsPastExamsCollapsed] = useState(false);
  const [isTeacherExamsCollapsed, setIsTeacherExamsCollapsed] = useState(false);

  // Determine if the user is from the UK
  const isUKUser = userData?.country === 'UK';
  logger.debug('ComprehensionView - isUKUser:', isUKUser, 'based on country:', userData?.country);

  // Fetch available comprehensions (only if initialComprehension is not provided)
  useEffect(() => {
    // Determine if fetching is needed
    const shouldFetch = !initialComprehension && userData?.role !== 'teacher';

    if (shouldFetch) {
      const fetchComprehensions = async () => {
        setIsLoading(true);
        setError(null); // Clear previous errors
        try {
          const response = await authenticatedFetch(`${API_BASE_URL}/api/comprehensions/available`);

          if (response.ok) {
            const data = await response.json();
            const list = Array.isArray(data) ? data : (data?.comprehensions || []);
            setComprehensions(list);
          } else {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch comprehensions');
          }
        } catch (err) {
          logger.error('Error fetching comprehensions:', err);
          setError(err.message || 'Failed to load comprehensions');
        } finally {
          setIsLoading(false);
        }
      };

      fetchComprehensions();
    } else {
      // If not fetching, ensure loading is false
      setIsLoading(false);
    }

  }, [API_BASE_URL, authToken, userData?.role, initialComprehension]);

  // Initialize answers if initialComprehension is provided (MOVED BEFORE TEACHER CHECK)
  useEffect(() => {
    if (initialComprehension && initialComprehension.questions) {
      const initialAns = {};
      initialComprehension.questions.forEach((_, index) => {
        initialAns[index] = '';
      });
      setAnswers(initialAns);
    }
  }, [initialComprehension]);

  // If user is a teacher, show teacher view
  if (userData?.role === 'teacher') {
    // This component is primarily for students or teachers answering their own.
    // The main teacher view (listing, creating) is in TeacherComprehensionView.
    // If a teacher gets here via initialComprehension, they are answering.
    // If they somehow get here without initialComprehension, maybe show an error or redirect?
    // For now, assuming TeacherComprehensionView handles the main teacher workflow.
    // If a teacher IS answering (initialComprehension is set), the logic proceeds below.
    // If not, maybe return null or an error message? Let's return null for now.
    if (!initialComprehension) {
        logger.warn("ComprehensionView rendered for teacher without initialComprehension data.");
        return null; // Or some placeholder/error
    }
  }

  const handleComprehensionSelect = (comprehension) => {
    track('comprehension_selected', {
      comprehension_id: comprehension.id,
      comprehension_title: comprehension.title,
      is_public: comprehension.isPublic
    });
    // Ensure questions are parsed
    const parsedComp = {
        ...comprehension,
        questions: typeof comprehension.questions === 'string'
            ? JSON.parse(comprehension.questions)
            : comprehension.questions
    };
    setSelectedComprehension(parsedComp);
    // Initialize answers object
    const initialAnswers = {};
    parsedComp.questions.forEach((_, index) => {
      initialAnswers[index] = '';
    });
    setAnswers(initialAnswers);
  };

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: value
    }));
  };

  const handleImageSelect = async (questionIndex, event) => {
    const file = event.target.files[0];
    if (!file) return;

    track('comprehension_image_upload_started', {
      question_index: questionIndex,
      file_type: file.type
    });

    try {
      // Show processing state
      setProcessingImages(prev => ({
        ...prev,
        [questionIndex]: true
      }));

      // Store file for preview
      setImageFiles(prev => ({
        ...prev,
        [questionIndex]: file
      }));

      // Create form data
      const formData = new FormData();
      formData.append('images', file);

      // Send for OCR processing
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ocr-images`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        track('comprehension_image_upload_success', {
          question_index: questionIndex,
          task_id: data.task_id
        });

        // Start polling for OCR results
        if (data.task_id) {
          pollOCRStatus(data.task_id, questionIndex);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process image');
      }
    } catch (err) {
      track('comprehension_image_upload_error', {
        question_index: questionIndex,
        error: err.message
      });
      logger.error('Error processing image:', err);
      setError('Failed to process image. Please try again or enter text manually.');
      setProcessingImages(prev => {
        const newProcessing = { ...prev };
        delete newProcessing[questionIndex];
        return newProcessing;
      });
    }
  };

  const pollOCRStatus = async (taskId, questionIndex) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ocr-status/${taskId}`);

      if (response.ok) {
        const data = await response.json();
        
        if (data.state === 'SUCCESS') {
          // Update answers with OCR result
          setAnswers(prev => ({
            ...prev,
            [questionIndex]: data.result.response
          }));
          setProcessingImages(prev => {
            const newProcessing = { ...prev };
            delete newProcessing[questionIndex];
            return newProcessing;
          });
        } else if (data.state === 'FAILURE') {
          throw new Error('OCR processing failed');
        } else {
          // Still processing, poll again after delay
          setTimeout(() => pollOCRStatus(taskId, questionIndex), 2000);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check OCR status');
      }
    } catch (err) {
      logger.error('Error polling OCR status:', err);
      setError('Failed to process image. Please try again or enter text manually.');
      setProcessingImages(prev => {
        const newProcessing = { ...prev };
        delete newProcessing[questionIndex];
        return newProcessing;
      });
    }
  };

  const handleRemoveImage = (questionIndex) => {
    setImageFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[questionIndex];
      return newFiles;
    });
  };

  const pollSubmissionStatus = async (subId) => {
    try {
      const statusResponse = await authenticatedFetch(`${API_BASE_URL}/api/submissions/${subId}/status`);

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        
        if (statusData.status === 'completed') {
          logger.info('Status is completed, fetching full details...');
          try {
              // Fetch full details now that it's complete
              const detailsResponse = await authenticatedFetch(`${API_BASE_URL}/api/submissions/${subId}/details`);
              if (detailsResponse.ok) {
                  const fullSubmissionData = await detailsResponse.json();

                  // Now we have the full data needed for the results view
                  setSubmissionStatus(fullSubmissionData); // Update local status with full data
                  setIsLoading(false); 
                  setSuccess(true); 
                  setLoadingMessage(''); 

                  if (onSubmissionComplete) {
                    logger.info("Polling complete, calling onSubmissionComplete with full details:", fullSubmissionData);
                    onSubmissionComplete(fullSubmissionData); // Pass FULL details
                  }
              } else {
                  const errorData = await detailsResponse.json();
                  throw new Error(errorData.error || 'Failed to fetch submission details');
              }
          } catch (detailsError) {
              logger.error('Error fetching submission details:', detailsError);
              setError('Failed to load submission results.');
              setIsLoading(false);
              setLoadingMessage('');
          }
        } else {
          // Continue polling if not complete
          setLoadingMessage('Grading your answers...'); 
          setTimeout(() => pollSubmissionStatus(subId), 2000); 
        }
      } else {
        const errorData = await statusResponse.json();
        throw new Error(errorData.error || 'Failed to check submission status');
      }
    } catch (err) {
      logger.error('Error polling submission status:', err);
      setError('Failed to check submission status. Please try again.');
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    track('comprehension_submit_started', {
      comprehension_id: selectedComprehension.id,
      comprehension_title: selectedComprehension.title,
      question_count: Object.keys(answers).length
    });

    setIsLoading(true);
    setError(null);
    setSuccess(false);
    setLoadingMessage('Submitting your answers...');

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/comprehensions/${selectedComprehension.id}/submit`, {
        method: 'POST',
        body: JSON.stringify({
          answers: Object.values(answers)
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.submission_id) {
          track('comprehension_submit_success', {
            comprehension_id: selectedComprehension.id,
            submission_id: result.submission_id
          });
          setSubmissionId(result.submission_id);
          // Start polling for status
          pollSubmissionStatus(result.submission_id);
        } else {
          throw new Error('No submission ID received');
        }
      } else {
        const errorData = await response.json();
        track('comprehension_submit_error', {
          comprehension_id: selectedComprehension.id,
          error: errorData.error || 'API Error',
          status: response.status
        });
        throw new Error('Failed to submit answers');
      }
    } catch (err) {
      track('comprehension_submit_error', {
        comprehension_id: selectedComprehension.id,
        error: err.message
      });
      setError(err.message);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // Add loading state for submission polling
  if (submissionStatus && submissionStatus !== 'complete' && submissionStatus !== 'error') {
    return <LoadingAnecdotes isUKUser={isUKUser} />;
  }

  // Main loading state - use !selectedComprehension as original
  if (isLoading && !selectedComprehension) {
    return (
      <div className="comprehension-view loading-view">
        <Loading />
      </div>
    );
  }

  if (error && !selectedComprehension) {
    return <div className="error-message">{error}</div>;
  }

  // Filter comprehensions for display
  const publicComprehensions = comprehensions
    .filter(comp => comp.isPublic)
    .sort((a, b) => b.title.localeCompare(a.title)); // Sort by title descending

  const teacherComprehensions = comprehensions
    .filter(comp => !comp.isPublic)
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); // Sort by createdAt ascending

  if (!selectedComprehension) {
    return (
      <div className="w-full max-w-6xl mx-auto p-6">
        <div className="space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-3xl font-bold text-foreground">Reading Comprehension</h1>
            <p className="text-muted-foreground text-lg">
              Choose a comprehension exercise to test your reading skills
            </p>
          </div>

          {/* Past Exams Section */}
          <Card className="overflow-hidden">
            <CardHeader 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => setIsPastExamsCollapsed(!isPastExamsCollapsed)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Past Exams</CardTitle>
                    <CardDescription>Practice with previous examination papers</CardDescription>
                  </div>
                </div>
                <ChevronRight className={cn(
                  "w-5 h-5 transition-transform duration-200",
                  isPastExamsCollapsed ? "" : "rotate-90"
                )} />
              </div>
            </CardHeader>
            {!isPastExamsCollapsed && (
              <CardContent className="pt-0">
                {publicComprehensions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No past exams available at the moment.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {publicComprehensions.map(comp => (
                      <Card 
                        key={comp.id}
                        className="group cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105 border-0 overflow-hidden"
                        onClick={() => handleComprehensionSelect(comp)}
                      >
                        <div className="h-full bg-gradient-to-br from-blue-500 to-blue-600 text-white relative">
                          <CardContent className="p-6 text-center space-y-4 relative z-10">
                            <div className="w-16 h-16 mx-auto rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl border border-white/30 group-hover:bg-white/30 transition-all duration-300">
                              📖
                            </div>
                            <div>
                              <h3 className="font-semibold text-white leading-tight mb-2">{comp.title}</h3>
                              <p className="text-white/80 text-sm mb-4">Public Exam</p>
                              <div className="flex items-center justify-center space-x-2 text-white/80 group-hover:text-white transition-colors">
                                <span className="text-sm">Start Reading</span>
                                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                              </div>
                            </div>
                          </CardContent>
                          
                          {/* Decorative elements */}
                          <div className="absolute top-3 right-3 w-12 h-12 rounded-full bg-white/10 -translate-y-6 translate-x-6"></div>
                          <div className="absolute bottom-3 left-3 w-8 h-8 rounded-full bg-white/10 translate-y-4 -translate-x-4"></div>
                          
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Teacher Comprehensions Section */}
          <Card className="overflow-hidden">
            <CardHeader 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => setIsTeacherExamsCollapsed(!isTeacherExamsCollapsed)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">From My Teacher</CardTitle>
                    <CardDescription>Assignments created by your teacher</CardDescription>
                  </div>
                </div>
                <ChevronRight className={cn(
                  "w-5 h-5 transition-transform duration-200",
                  isTeacherExamsCollapsed ? "" : "rotate-90"
                )} />
              </div>
            </CardHeader>
            {!isTeacherExamsCollapsed && (
              <CardContent className="pt-0">
                {teacherComprehensions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No assignments from your teacher yet.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teacherComprehensions.map(comp => (
                      <Card 
                        key={comp.id}
                        className="group cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105 border-0 overflow-hidden"
                        onClick={() => handleComprehensionSelect(comp)}
                      >
                        <div className="h-full bg-gradient-to-br from-green-500 to-green-600 text-white relative">
                          <CardContent className="p-6 text-center space-y-4 relative z-10">
                            <div className="w-16 h-16 mx-auto rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl border border-white/30 group-hover:bg-white/30 transition-all duration-300">
                              🎓
                            </div>
                            <div>
                              <h3 className="font-semibold text-white leading-tight mb-2">{comp.title}</h3>
                              <p className="text-white/80 text-sm mb-4">By: {comp.teacherName}</p>
                              <div className="flex items-center justify-center space-x-2 text-white/80 group-hover:text-white transition-colors">
                                <span className="text-sm">Start Assignment</span>
                                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                              </div>
                            </div>
                          </CardContent>
                          
                          {/* Decorative elements */}
                          <div className="absolute top-3 right-3 w-12 h-12 rounded-full bg-white/10 -translate-y-6 translate-x-6"></div>
                          <div className="absolute bottom-3 left-3 w-8 h-8 rounded-full bg-white/10 translate-y-4 -translate-x-4"></div>
                          
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      {isLoading && <LoadingAnecdotes />}
      
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => {
              if (onBack) {
                onBack();
              } else {
                setSelectedComprehension(null);
                setError(null);
                setSuccess(false);
                setImageFiles({});
                setAnswers({});
                setSubmissionId(null);
                setSubmissionStatus(null);
                setLoadingMessage('');
              }
            }}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Comprehensions</span>
          </Button>
        </div>

        {/* Title */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-foreground">{selectedComprehension.title}</h1>
          <p className="text-muted-foreground">Read the passage and answer the questions below</p>
        </div>

        {/* Status messages */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2 text-red-800">
                <X className="w-5 h-5" />
                <p className="font-medium">Error</p>
              </div>
              <p className="text-red-700 mt-1">{error}</p>
            </CardContent>
          </Card>
        )}

        {success && submissionStatus && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2 text-green-800">
                <Award className="w-5 h-5" />
                <p className="font-medium">Submission Complete!</p>
              </div>
              <p className="text-green-700 mt-1">Your score: {submissionStatus.score}</p>
            </CardContent>
          </Card>
        )}

        {loadingMessage && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <p className="text-blue-800 font-medium">{loadingMessage}</p>
              </div>
            </CardContent>
          </Card>
        )}

                 {/* Reading passage */}
         <Card>
           <CardHeader>
             <CardTitle className="flex items-center space-x-2">
               <FileText className="w-5 h-5" />
               <span>Reading Passage</span>
             </CardTitle>
           </CardHeader>
           <CardContent>
             <div className="prose max-w-none text-foreground leading-relaxed space-y-4">
               {selectedComprehension.text.split('\n').filter(para => para.trim()).map((paragraph, index) => (
                 <p key={index} className="text-base leading-7">
                   {paragraph.trim()}
                 </p>
               ))}
             </div>
           </CardContent>
         </Card>

        {/* Questions form */}
        <Card>
          <CardHeader>
            <CardTitle>Questions</CardTitle>
            <CardDescription>
              Answer all questions based on the reading passage above
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {selectedComprehension.questions.map((question, index) => (
                <Card key={index} className="border-muted">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Question {index + 1}</CardTitle>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {question.points} points
                      </span>
                    </div>
                    <CardDescription className="text-base font-medium text-foreground">
                      {question.text}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <textarea
                        value={answers[index] || ''}
                        onChange={(e) => handleAnswerChange(index, e.target.value)}
                        placeholder="Write your answer here..."
                        className="w-full min-h-32 p-4 border border-muted rounded-lg resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        disabled={processingImages[index]}
                      />
                      
                      {/* Image upload section */}
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageSelect(index, e)}
                            id={`image-upload-${index}`}
                            className="hidden"
                            disabled={processingImages[index]}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => document.getElementById(`image-upload-${index}`).click()}
                            disabled={processingImages[index]}
                            className="flex items-center space-x-2"
                          >
                            <Upload className="w-4 h-4" />
                            <span>Upload Image</span>
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            or type your answer above
                          </span>
                        </div>

                        {imageFiles[index] && (
                          <Card className="p-4">
                            <div className="flex items-start space-x-4">
                              <img
                                src={URL.createObjectURL(imageFiles[index])}
                                alt={`Preview for question ${index + 1}`}
                                className="w-32 h-32 object-cover rounded-lg border"
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium mb-2">Image uploaded</p>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRemoveImage(index)}
                                  disabled={processingImages[index]}
                                  className="flex items-center space-x-1"
                                >
                                  <X className="w-3 h-3" />
                                  <span>Remove</span>
                                </Button>
                              </div>
                            </div>
                          </Card>
                        )}

                        {processingImages[index] && (
                          <Card className="border-blue-200 bg-blue-50">
                            <CardContent className="p-4">
                              <div className="flex items-center space-x-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                <span className="text-blue-800 font-medium">Processing image...</span>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <div className="flex justify-center pt-6">
                <Button
                  type="submit"
                  size="lg"
                  disabled={isLoading || Object.keys(processingImages).length > 0}
                  className="px-8 py-3 text-lg"
                >
                  {loadingMessage || 
                   (Object.keys(processingImages).length > 0 ? 'Processing Images...' : 
                   'Submit Answers')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ComprehensionView; 